import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { DocumentModel, FormOption } from '@/lib/models';
import { blogsService } from '@/lib/services';
import { useAccount, useHandleError, useQuery } from '@/hooks';
import { AsyncPage, FeaturedContentTile, Loader, LoadingButton, Select } from '@/components';
import { Helmet } from 'react-helmet';
import { constructUrl } from '@/lib/utils';
import { createUseStyles } from 'react-jss';
import { mediaQueries } from '@/jss';

const useStyles = createUseStyles({
	title: {
		marginBottom: 20,
		[mediaQueries.md]: {
			marginBottom: 0,
		},
	},
	selectOuter: {
		flex: 1,
		[mediaQueries.md]: {
			flex: 'none',
		},
	},
});

const pageSize = 9;

export const BlogPostsPublic = () => {
	const handleError = useHandleError();
	const pageRef = useRef(0);
	const { account } = useAccount();
	const [isLoading, setIsLoading] = useState(false);
	const [blogs, setBlogs] = useState<DocumentModel[]>([]);
	const [blogsTotalCount, setBlogsTotalCount] = useState(0);

	const classes = useStyles();
	const searchParams = useQuery();
	const topicId = useMemo(() => searchParams.get('topics') ?? '', [searchParams]);
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const [filterOptions, setFilterOptions] = useState<FormOption[]>([]);

	const fetchFilters = useCallback(async () => {
		const { topics } = await blogsService.getPublicBlogFilters().fetch();
		setFilterOptions(topics);
	}, []);

	const fetchBlogs = useCallback(async () => {
		setIsLoading(true);

		try {
			const response = await blogsService
				.getPublicBlogs({
					size: pageSize,
					page: pageRef.current,
					...(topicId ? { topicIds: [topicId] } : {}),
				})
				.fetch();

			setBlogsTotalCount(response.totalCount);
			setBlogs((previousValue) => {
				if (pageRef.current === 0) {
					return response.results;
				}

				return previousValue.concat(response.results);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [topicId, handleError]);

	useEffect(() => {
		fetchBlogs();
	}, [fetchBlogs]);

	const handleFilterChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		setBlogs([]);
		setBlogsTotalCount(0);
		navigate(constructUrl(pathname, { ...(currentTarget.value ? { topics: currentTarget.value } : {}) }), {
			replace: true,
		});
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		fetchBlogs();
	};

	// If an authenticated account is present, redirect to the authenticated blogs page.
	if (account) {
		return <Navigate to="/research?contentTypes=BLOG" replace />;
	}

	return (
		<>
			<Helmet>
				<title>Public Blogs</title>
			</Helmet>
			<AsyncPage fetchData={fetchFilters}>
				<Container className="py-10">
					<Row className="mb-11">
						<Col md={6}>
							<h1 className={classes.title}>Blog Posts</h1>
						</Col>
						<Col md={6}>
							<div className={'d-flex align-items-center justify-content-end'}>
								<Form.Label htmlFor="filter-select" className="text-nowrap mb-0 mr-2 text-muted">
									Filter By:
								</Form.Label>
								<Select
									id="filter-select"
									className={classes.selectOuter}
									value={topicId}
									onChange={handleFilterChange}
								>
									<option value="">All Topics</option>
									{filterOptions.map((option) => {
										return (
											<option key={option.id} value={option.id}>
												{option.display}
											</option>
										);
									})}
								</Select>
							</div>
						</Col>
					</Row>
					{isLoading && blogs.length <= 0 && (
						<Row>
							<Col>
								<div className="d-flex justify-content-center">
									<Loader />
								</div>
							</Col>
						</Row>
					)}
					{!isLoading && blogs.length === 0 && (
						<Row>
							<Col>
								<div className="p-10 rounded border bg-white">
									<h3 className="mb-2 text-center">No public blog posts</h3>
									<p className="mb-0 text-muted text-center">
										When public blogs are posted, they will appear here.
									</p>
								</div>
							</Col>
						</Row>
					)}
					{blogs.length > 0 && (
						<Row>
							{blogs.map((blog) => {
								return (
									<Col key={blog.documentId} className="mb-6" sm={12} md={6} lg={4}>
										<FeaturedContentTile
											to={`/public/blogs/${blog.documentId}`}
											imageUrl={blog.imageUrl}
											title={blog.name}
											description={blog.contentDescription}
										/>
									</Col>
								);
							})}
						</Row>
					)}
					{blogs.length < blogsTotalCount && (
						<Row>
							<Col>
								<div className="text-center">
									<LoadingButton
										isLoading={isLoading}
										variant="outline-primary"
										onClick={handleShowMoreButtonClick}
									>
										Show More
									</LoadingButton>
								</div>
							</Col>
						</Row>
					)}
				</Container>
			</AsyncPage>
		</>
	);
};
